import React from 'react';
import { useLocation } from 'react-router-dom';
import AppRoutes from './routes/AppRoutes';
import Footer, {
  APP_FOOTER_DESKTOP_OFFSET,
  appMainFooterClearanceSx,
} from './components/Footer';
import { Box, useMediaQuery } from '@mui/material';
import { useAppTheme } from './theme';
import type { ThemedSxStyle } from './types/themedStyles';
import './i18n/i18n';
import { TeamUpdateLiveNotifier } from './components/team-updates/TeamUpdateLiveNotifier';
import { isOccasionLandingPath } from './utilities/occasionPortal';
import { isJobsPublicPath } from './utilities/jobsPortal';
import { isFacilitatorContractPublicPath } from './utilities/facilitatorContracts';
import { isConsentPublicPath } from './utilities/consentForm';
import { isWorkExperiencePublicPath } from './utilities/workExperienceWorksheet';
import { clearStaleLazyChunkReloadFlag } from './utilities/staleLazyChunkRecovery';

const App: React.FC = () => {
  const { theme, themed } = useAppTheme();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:899px)');
  const occasionLanding = isOccasionLandingPath(location.pathname);
  const jobsPublic = isJobsPublicPath(location.pathname);
  const contractPublic = isFacilitatorContractPublicPath(location.pathname);
  const consentPublic = isConsentPublicPath(location.pathname);
  const workExperiencePublic = isWorkExperiencePublicPath(location.pathname);
  const lightPublicPage = occasionLanding || jobsPublic || contractPublic || consentPublic || workExperiencePublic;
  const presentationLink = location.pathname.startsWith('/present/');
  const hideFooter = isMobile || presentationLink;
  const pageBackground = lightPublicPage ? '#ffffff' : theme.colors.background;

  React.useEffect(() => {
    clearStaleLazyChunkReloadFlag();
  }, []);

  React.useEffect(() => {
    const root = document.documentElement;
    const prevBodyBg = document.body.style.backgroundColor;
    const prevHtmlBg = root.style.backgroundColor;
    const prevColorScheme = root.style.colorScheme;
    const prevRootBg = document.getElementById('root')?.style.backgroundColor ?? '';

    document.body.style.backgroundColor = pageBackground;
    root.style.backgroundColor = pageBackground;
    root.style.colorScheme = lightPublicPage || !theme.isDark ? 'light' : 'dark';
    const rootEl = document.getElementById('root');
    if (rootEl) {
      rootEl.style.backgroundColor = pageBackground;
    }

    return () => {
      document.body.style.backgroundColor = prevBodyBg;
      root.style.backgroundColor = prevHtmlBg;
      root.style.colorScheme = prevColorScheme;
      if (rootEl) {
        rootEl.style.backgroundColor = prevRootBg;
      }
    };
  }, [pageBackground, lightPublicPage, theme.isDark]);

  return (
    <Box
      sx={themed([
        lightPublicPage ? $sxAppShellLight : $sxAppShell,
        $sxFooterOffsetVar(hideFooter),
      ])}
    >
      <Box sx={themed(hideFooter ? $sxMainNoFooter : appMainFooterClearanceSx)}>
        <AppRoutes />
      </Box>
      {hideFooter ? null : <Footer light={lightPublicPage} />}
      <TeamUpdateLiveNotifier />
    </Box>
  );
};

const $sxAppShell: ThemedSxStyle = ({ colors }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  backgroundColor: colors.background,
  color: colors.text,
});

const $sxAppShellLight: ThemedSxStyle = () => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  backgroundColor: '#ffffff',
  color: '#1a1f26',
});

const $sxMainNoFooter: ThemedSxStyle = () => ({
  flex: 1,
});

const $sxFooterOffsetVar = (hideFooter: boolean): ThemedSxStyle => () => ({
  '--app-footer-offset': hideFooter
    ? 'env(safe-area-inset-bottom, 0px)'
    : APP_FOOTER_DESKTOP_OFFSET,
});

export default App;
